/**
 * VIN Pulse OS - Protected Route Component
 * Enforces authentication and authorization (RBAC) on routes
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  /** Set on the forced-password-reset route itself to avoid a redirect loop. */
  allowPasswordChange?: boolean;
}

/**
 * Wraps a route to enforce authentication
 * Optionally enforces role-based access control (RBAC)
 */
export function ProtectedRoute({
  children,
  requiredRoles,
  allowPasswordChange,
}: ProtectedRouteProps) {
  const { isSignedIn, user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isSignedIn || !user) {
    return <Navigate to="/login" replace />;
  }

  // Onboarded customers must set their own password before doing anything else.
  // The reset route passes allowPasswordChange so it can render without looping.
  if (user.must_change_password && !allowPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }

  // Check RBAC if required roles are specified (case-insensitive)
  if (requiredRoles && requiredRoles.length > 0) {
    if (!requiredRoles.map(r => r.toLowerCase()).includes(user.role?.toLowerCase())) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <div>
            <h1>Access Denied</h1>
            <p>Your role ({user.role}) does not have access to this page.</p>
            <p>Required roles: {requiredRoles.join(', ')}</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}

/**
 * Conditionally renders children based on authentication and role
 * Useful for rendering parts of the UI based on user role
 */
export function RoleBasedUI({
  children,
  requiredRoles,
}: {
  children: React.ReactNode;
  requiredRoles?: string[];
}) {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  if (requiredRoles && requiredRoles.length > 0) {
    if (!requiredRoles.map(r => r.toLowerCase()).includes(user.role?.toLowerCase())) {
      return null;
    }
  }

  return <>{children}</>;
}
